/**
 * TokenEstimateCard Component
 *
 * Shows estimated token cost for an operation before execution.
 * Displays cloud vs local GPU pricing with warnings if insufficient tokens.
 *
 * Visual pattern: right-aligned stacked layout (label on top, value below).
 * Matches SfxDurationSlider cost display for visual consistency.
 */

import { memo, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useTokenBalance } from '../../hooks/useTokenBalance';
import {
  COST_GATE_UNAVAILABLE,
  costGateStatus,
  type CostGate,
  type CostGateStatus,
} from '../../utils/costGate';
import { handleError } from '../../utils/errorHandler';
import { isServerlessMode, isElectronMode } from '../../utils/apiMode';
import { previewCost, type CostEstimate } from '../../services/costPreview';
import { CloudLocalCostDisplay } from './CloudLocalCostDisplay';

// ============================================================================
// Types
// ============================================================================

interface TokenEstimateCardProps {
  /**
   * OpenAPI path of the operation being priced, exactly as
   * GET /api/v1/capabilities publishes it (e.g. '/api/v1/generate/tts').
   * Omit when using staticEstimate.
   */
  operationPath?: string;
  /** HTTP method of that operation. Every priced operation is a POST today. */
  operationMethod?: string;
  /** Parameters for the operation (varies by operation type). Omit when using staticEstimate. */
  parameters?: Record<string, unknown>;
  /**
   * Pre-computed token cost (e.g. comes from a model-registry entry).
   * When provided, the component skips the /tokens/estimate fetch and renders
   * this value directly — letting flows with a known static price reuse the
   * same visual style as fetch-driven estimates.
   */
  staticEstimate?: number;
  /** Whether the action may go ahead at this cost (src/utils/costGate.ts). */
  onEstimateChange?: (gate: CostGate) => void;
  /** Callback when loading state changes, useful for disabling submit buttons */
  onLoadingChange?: (isLoading: boolean) => void;
  /**
   * When explicitly provided (true OR false), this OVERRIDES the backend
   * estimate's `is_local_provider` flag. The caller has authoritative,
   * real-time knowledge of whether the SPECIFIC selected provider is actually
   * installed/running locally (e.g. from useImageProviderAvailability /
   * useComponentStatus), whereas the backend flag for hybrid providers (local
   * install optional, cloud fallback available — e.g. Fooocus -> RunPod) is a
   * static "this operation TYPE has a local execution path" capability check
   * that cannot see the desktop's actual install state.
   *
   * Omit this prop (leave it `undefined`) to fall back to the backend's
   * capability flag — appropriate for operations that always run locally on
   * desktop once the (bundled, non-optional) local stage is present, e.g.
   * WhisperX/NLLB/dubbing.
   *
   * IMPORTANT: this must be a real override (undefined vs boolean), not an
   * OR with the backend flag — a caller passing `false` to say "confirmed NOT
   * installed" must be able to defeat a backend flag that defaults to `true`.
   */
  isLocalProvider?: boolean;
}

/**
 * Resolve whether the given estimate should be billed/displayed at the local
 * GPU rate. `callerIsLocalProvider` (the `isLocalProvider` prop), when given
 * as an actual boolean, takes full precedence over `estimate.is_local_provider`
 * — see the prop's doc-comment for why this must be an override, not an OR.
 */
function resolveRunsLocally(
  callerIsLocalProvider: boolean | undefined,
  estimate: CostEstimate
): boolean {
  return typeof callerIsLocalProvider === 'boolean'
    ? callerIsLocalProvider
    : estimate.is_local_provider === true;
}

// ============================================================================
// Component
// ============================================================================

export const TokenEstimateCard = memo(function TokenEstimateCard({
  operationPath,
  operationMethod = 'POST',
  parameters,
  staticEstimate,
  onEstimateChange,
  onLoadingChange,
  isLocalProvider,
}: TokenEstimateCardProps) {
  const { t } = useTranslation();
  const { balance } = useTokenBalance();
  // Billing is only enforced where the /api/v1/tokens/* routes exist (Cloud Run
  // serverless and Electron desktop, which proxies to Cloud Run). In pure
  // local web dev (npm run dev → local Flask) the balance fetch is skipped, so
  // gating on balance would falsely block every paid action.
  const billingEnforced = isServerlessMode() || isElectronMode();
  const staticEstimateData = useMemo<CostEstimate | null>(
    () =>
      typeof staticEstimate === 'number' && staticEstimate >= 0
        ? {
            estimated_tokens: staticEstimate,
            estimated_cost_usd: 0,
            local_gpu_tokens: 0,
            local_gpu_cost_usd: 0,
            processing_seconds: 0,
          }
        : null,
    [staticEstimate]
  );

  // Tokens actually charged for an estimate: the local price on desktop when the
  // provider runs on the user's own GPU, otherwise the cloud price. Single SSOT
  // for both the gating callback (onEstimateChange) and the displayed cost.
  const effectiveTokens = useCallback(
    (est: CostEstimate): number => {
      const runsLocally = resolveRunsLocally(isLocalProvider, est);
      return isElectronMode() && runsLocally ? (est.local_gpu_tokens ?? 0) : est.estimated_tokens;
    },
    [isLocalProvider]
  );

  // Where billing is not enforced (pure local web dev) nothing is ever short.
  const gateStatus = useCallback(
    (charged: number, knownBalance: number | null): CostGateStatus =>
      billingEnforced ? costGateStatus(charged, knownBalance) : 'affordable',
    [billingEnforced]
  );

  const [estimate, setEstimate] = useState<CostEstimate | null>(staticEstimateData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track if component is mounted to avoid setting state after unmount
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Use refs for callback props and balance to prevent them from triggering re-fetches.
  // Without refs, any parent re-render creates new callback instances,
  // which changes fetchEstimate, which restarts the debounce timer → infinite loop.
  const onEstimateChangeRef = useRef(onEstimateChange);
  onEstimateChangeRef.current = onEstimateChange;
  const onLoadingChangeRef = useRef(onLoadingChange);
  onLoadingChangeRef.current = onLoadingChange;
  // Balance ref avoids including balance in fetchEstimate deps (prevents spurious API calls on balance poll)
  const balanceRef = useRef(balance);
  balanceRef.current = balance;

  // Notify parent when loading state changes (via ref to avoid dep cycle)
  useEffect(() => {
    onLoadingChangeRef.current?.(loading);
  }, [loading]);

  // Debounce parameters to avoid excessive API calls
  // Memoize the serialized parameters to ensure stable dependency
  const paramsString = useMemo(() => JSON.stringify(parameters), [parameters]);

  const fetchEstimate = useCallback(async () => {
    if (staticEstimateData) {
      // Static path: render the pre-computed cost without hitting the API.
      setEstimate(staticEstimateData);
      const charged = effectiveTokens(staticEstimateData);
      onEstimateChangeRef.current?.({
        tokens: charged,
        status: gateStatus(charged, balanceRef.current),
      });
      return;
    }
    if (!operationPath) return;
    // POST /api/v1/cost/preview is served by BOTH backends now, but this card
    // reads a token BALANCE alongside the quote and billing lives on Cloud Run.
    // Fetch in web serverless mode and in Electron desktop (which routes via
    // VITE_CLOUD_API_URL); skip in pure local web dev, where buildCloudApiUrl
    // has no cloud target to resolve.
    if (!isServerlessMode() && !isElectronMode()) return;

    setLoading(true);
    setError(null);

    try {
      const estimateData = await previewCost(
        operationPath,
        JSON.parse(paramsString),
        operationMethod
      );

      if (!isMounted.current) return;

      setEstimate(estimateData);

      // Notify parent of estimate change (via ref to avoid dep cycle).
      // Report the EFFECTIVE charge (local price on desktop-local) so a free
      // local op is never blocked by a low cloud-token balance.
      const charged = effectiveTokens(estimateData);
      onEstimateChangeRef.current?.({
        tokens: charged,
        status: gateStatus(charged, balanceRef.current),
      });
    } catch (err) {
      if (isMounted.current) {
        setError(handleError(err, 'TokenEstimate', t('tokens.estimateFailed')));
        // Block the action -- nobody can say what it would charge -- but as
        // "cost unavailable", never as "insufficient tokens".
        onEstimateChangeRef.current?.(COST_GATE_UNAVAILABLE);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [
    operationPath,
    operationMethod,
    paramsString,
    staticEstimateData,
    gateStatus,
    effectiveTokens,
    t,
  ]);

  // Debounced fetch when parameters change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchEstimate();
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [fetchEstimate]);

  // Update parent when balance changes (via ref to avoid dep cycle)
  useEffect(() => {
    if (estimate) {
      const charged = effectiveTokens(estimate);
      onEstimateChangeRef.current?.({ tokens: charged, status: gateStatus(charged, balance) });
    }
  }, [balance, estimate, gateStatus, effectiveTokens]);

  // Loading state
  if (loading && !estimate) {
    return (
      <div className="text-right">
        <div className="text-xs text-white/40">{t('tokens.cost', { defaultValue: 'Cost' })}</div>
        <div className="flex items-center justify-end gap-2 mt-0.5">
          <Loader2 className="h-4 w-4 animate-spin text-white/40" />
          <span className="text-sm text-white/50">{t('tokens.calculating')}</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-right">
        <p className="text-red-400 text-sm">{error}</p>
      </div>
    );
  }

  // No estimate yet
  if (!estimate) return null;

  const tokenPrefix = estimate.is_ceiling ? t('tokens.upTo', { defaultValue: 'Up to' }) + ' ' : '';

  // Dual "Cloud X / Local GPU Free" on desktop when the provider runs on the
  // user's own GPU; single cloud price on web or for cloud-only providers. The
  // isLocalProvider prop (tab knows locality from the selected provider) takes
  // precedence over the backend estimate flag. Same SSOT used for the gating.
  const runsLocally = resolveRunsLocally(isLocalProvider, estimate);
  const showLocal = isElectronMode() && runsLocally;
  const charged = showLocal ? (estimate.local_gpu_tokens ?? 0) : estimate.estimated_tokens;
  const status = gateStatus(charged, balance);
  const hasEnough = status !== 'insufficient';

  return (
    <div>
      <CloudLocalCostDisplay
        cloudTokens={estimate.estimated_tokens}
        localTokens={estimate.local_gpu_tokens ?? 0}
        showLocal={showLocal}
        hasEnough={hasEnough}
        prefix={tokenPrefix}
        loading={loading}
      />

      {/* Warning if not enough tokens */}
      {status === 'insufficient' && balance !== null && (
        <div className="flex items-center gap-1.5 text-red-400 text-xs justify-end mt-1">
          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
          <span>{t('tokens.needMoreTokens', { count: charged - balance })}</span>
        </div>
      )}
    </div>
  );
});
